import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// Convert hex to HSL string for CSS variables
function hexToHSL(hex) {
  hex = hex.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function darken(hex, pct) {
  hex = hex.replace('#', '');
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  r = Math.max(0, Math.round(r * (1 - pct)));
  g = Math.max(0, Math.round(g * (1 - pct)));
  b = Math.max(0, Math.round(b * (1 - pct)));
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}

function lighten(hex, pct) {
  hex = hex.replace('#', '');
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  r = Math.min(255, Math.round(r + (255 - r) * pct));
  g = Math.min(255, Math.round(g + (255 - g) * pct));
  b = Math.min(255, Math.round(b + (255 - b) * pct));
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}

function applyTheme(colors) {
  if (!colors) return;
  const root = document.documentElement;
  const bg = colors.background || '#FDFBF7';
  const primary = colors.primary || '#1E3A2B';
  const accent = colors.accent || '#D95D39';
  const textPrimary = colors.text_primary || '#1A1918';
  const card = colors.card || '#FFFFFF';
  const border = colors.border || lighten(textPrimary, 0.85);
  const subtle = colors.subtle || lighten(bg, 0.02);

  root.style.setProperty('--background', hexToHSL(bg));
  root.style.setProperty('--foreground', hexToHSL(textPrimary));
  root.style.setProperty('--card', hexToHSL(card));
  root.style.setProperty('--card-foreground', hexToHSL(textPrimary));
  root.style.setProperty('--popover', hexToHSL(card));
  root.style.setProperty('--popover-foreground', hexToHSL(textPrimary));
  root.style.setProperty('--primary', hexToHSL(primary));
  root.style.setProperty('--primary-foreground', hexToHSL(colors.primary_foreground || '#FFFFFF'));
  root.style.setProperty('--secondary', hexToHSL(subtle));
  root.style.setProperty('--secondary-foreground', hexToHSL(textPrimary));
  root.style.setProperty('--muted', hexToHSL(subtle));
  root.style.setProperty('--muted-foreground', hexToHSL(colors.text_muted || lighten(textPrimary, 0.5)));
  root.style.setProperty('--accent', hexToHSL(accent));
  root.style.setProperty('--accent-foreground', hexToHSL(colors.accent_foreground || '#FFFFFF'));
  root.style.setProperty('--destructive', hexToHSL(colors.error || '#E63946'));
  root.style.setProperty('--destructive-foreground', hexToHSL('#FFFFFF'));
  root.style.setProperty('--border', hexToHSL(border));
  root.style.setProperty('--input', hexToHSL(border));
  root.style.setProperty('--ring', hexToHSL(primary));
  root.style.setProperty('--chart-1', hexToHSL(primary));
  root.style.setProperty('--chart-2', hexToHSL(accent));
  root.style.setProperty('--chart-3', hexToHSL(colors.info || '#457B9D'));
  root.style.setProperty('--chart-4', hexToHSL(colors.warning || '#F4A261'));
  root.style.setProperty('--chart-5', hexToHSL(colors.success || '#2D6A4F'));
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appSettings, setAppSettings] = useState({
    app_name: 'Branch Manager',
    tagline: 'Insurance Operations Platform'
  });

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/app`);
      setAppSettings(data);
      // Apply active theme
      if (data.active_theme_id) {
        try {
          const themes = await axios.get(`${API_URL}/api/themes`);
          const active = themes.data.find(t => t.id === data.active_theme_id);
          if (active?.colors) applyTheme(active.colors);
        } catch (e) {
          console.error('Failed to apply theme');
        }
      }
    } catch (e) {
      console.error('Failed to fetch app settings');
    }
  }, []);

  useEffect(() => {
    fetchMe();
    fetchSettings();
  }, [fetchMe, fetchSettings]);

  const login = async (email, password, totpCode = null) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    
    const { data } = await axios.post(`${API_URL}/api/auth/login`, payload);
    if (data.requires_2fa) {
      return { requires_2fa: true };
    }
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      name
    });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (e) {
      console.error('Logout error', e);
    }
    setUser(false);
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/refresh`);
      await fetchMe();
    } catch (e) {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      refreshToken, 
      fetchMe,
      appSettings,
      setAppSettings,
      fetchSettings
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
